import { useState, useCallback, useEffect } from 'react';
import { arrayMove } from '@dnd-kit/sortable';

const MAX_PINNED = 5;
const STORAGE_KEY = 'pinnedFeatures';
const SYNC_EVENT = 'pinned-features-changed';
const DEFAULT_PINNED = ['reviews', 'campaigns', 'pipeline'];

function loadPinned(): string[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw === null) return DEFAULT_PINNED;
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.slice(0, MAX_PINNED) : DEFAULT_PINNED;
	} catch {
		return DEFAULT_PINNED;
	}
}

function savePinned(ids: string[]): void {
	localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
	window.dispatchEvent(new CustomEvent(SYNC_EVENT));
}

export function usePinnedFeatures() {
	const [pinnedIds, setPinnedIds] = useState<string[]>(() => loadPinned());

	useEffect(() => {
		const sync = () => setPinnedIds(loadPinned());
		window.addEventListener(SYNC_EVENT, sync);
		return () => window.removeEventListener(SYNC_EVENT, sync);
	}, []);

	const togglePin = useCallback((featureId: string) => {
		setPinnedIds((prev) => {
			const next = prev.includes(featureId)
				? prev.filter((id) => id !== featureId)
				: prev.length >= MAX_PINNED
					? prev
					: [...prev, featureId];
			savePinned(next);
			return next;
		});
	}, []);

	const unpin = useCallback((featureId: string) => {
		setPinnedIds((prev) => {
			const next = prev.filter((id) => id !== featureId);
			savePinned(next);
			return next;
		});
	}, []);

	const reorder = useCallback((activeId: string, overId: string) => {
		setPinnedIds((prev) => {
			const oldIndex = prev.indexOf(activeId);
			const newIndex = prev.indexOf(overId);
			if (oldIndex === -1 || newIndex === -1) return prev;
			const next = arrayMove<string>(prev, oldIndex, newIndex);
			savePinned(next);
			return next;
		});
	}, []);

	return { pinnedIds, togglePin, unpin, reorder };
}
